import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FC } from "react";
import { Cell, Pie, PieChart as RechartsPieChart, Tooltip } from "recharts";

import { NoSsr } from "~ui-components/components/atoms/no-ssr";
import { ChartFormValues } from "../../text-editor/chart-menu";
import { generateColorRange } from "../utils/generate-color-range";

const CHART_HEIGHT = 300;
const X_DATA_KEY = "name";
const Y_DATA_KEY = "value";
const CHART_THICKNESS = 30;

export const PieChart: FC<{
	dataValue: ChartFormValues;
	valuesToUsed: Record<string, any>;
}> = ({ dataValue, valuesToUsed }) => {
	const data = dataValue.attributes.map((att) => ({
		[X_DATA_KEY]: att.label,
		[Y_DATA_KEY]: valuesToUsed[att.key],
	}));
	const colorRanges = generateColorRange(
		dataValue.startColour!,
		dataValue.endColour!,
		data.length,
	);

	return (
		<NoSsr
			fallback={
				<Box
					sx={{
						height: `${CHART_HEIGHT}px`,
						width: `${CHART_HEIGHT}px`,
					}}
				/>
			}>
			<Stack alignItems="center">
				<RechartsPieChart
					height={CHART_HEIGHT}
					margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
					width={CHART_HEIGHT}>
					<Pie
						animationDuration={300}
						cx={CHART_HEIGHT / 2}
						cy={CHART_HEIGHT / 2}
						data={data}
						dataKey={Y_DATA_KEY}
						innerRadius={CHART_HEIGHT / 2 - CHART_THICKNESS}
						nameKey={X_DATA_KEY}
						outerRadius={CHART_HEIGHT / 2}
						strokeWidth={0}>
						{data.map((entry, i) => (
							<Cell
								key={entry.name}
								fill={colorRanges[i]}
							/>
						))}
					</Pie>
					<Tooltip
						animationDuration={50}
						content={<TooltipContent />}
					/>
				</RechartsPieChart>
				<Legend
					payload={data.map((entry, i) => ({
						...entry,
						color: colorRanges[i]!,
					}))}
				/>
			</Stack>
		</NoSsr>
	);
};

interface LegendProps {
	payload?: { name: string; value: number; color: string }[];
}

function Legend({ payload }: LegendProps) {
	return (
		<Box
			sx={{
				width: CHART_HEIGHT,
				display: "grid",
				gap: 3,
				gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
			}}>
			{payload?.map((entry) => (
				<div key={entry.name}>
					<Stack
						direction="row"
						spacing={1}
						sx={{ alignItems: "center" }}>
						<Box
							sx={{
								bgcolor: entry.color,
								borderRadius: "2px",
								height: "4px",
								width: "16px",
							}}
						/>
						<Typography variant="body2">{entry.name}</Typography>
					</Stack>
					<Typography variant="h5">
						{new Intl.NumberFormat("en-US").format(entry.value)}
					</Typography>
				</div>
			))}
		</Box>
	);
}

interface TooltipContentProps {
	active?: boolean;
	payload?: { fill: string; name: string; value: number }[];
	label?: string;
}

function TooltipContent({ active, payload }: TooltipContentProps) {
	if (!active) {
		return null;
	}

	return (
		<Paper
			sx={{
				border: "1px solid var(--mui-palette-divider)",
				boxShadow: "var(--mui-shadows-16)",
				p: 1,
			}}>
			<Stack spacing={2}>
				{payload?.map((entry) => (
					<Stack
						direction="row"
						key={entry.name}
						spacing={3}
						sx={{ alignItems: "center" }}>
						<Stack
							direction="row"
							spacing={1}
							sx={{ alignItems: "center", flex: "1 1 auto" }}>
							<Box
								sx={{
									bgcolor: entry.fill,
									borderRadius: "2px",
									height: "8px",
									width: "8px",
								}}
							/>
							<Typography sx={{ whiteSpace: "nowrap" }}>
								{entry.name}
							</Typography>
						</Stack>
						<Typography
							color="text.secondary"
							variant="body2">
							{new Intl.NumberFormat("en-US").format(entry.value)}
						</Typography>
					</Stack>
				))}
			</Stack>
		</Paper>
	);
}
